import { Component, Input, forwardRef, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { MatDateRangeInput } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';

interface DateRange {
  start: Date | null;
  end: Date | null;
}

@Component({
  selector: 'flow-form-date-range-picker',
  templateUrl: './form-date-range-picker.component.html',
  styleUrls: ['./form-date-range-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormDateRangePickerComponent),
      multi: true,
    },
  ],
})
export class FormDateRangePickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  destroy$ = new Subject<void>();

  @ViewChild(MatDateRangeInput) dateRangeInput: MatDateRangeInput<Date>;
  @Input() label: string;
  @Input() fromPlaceholder: string = 'global.startDate';
  @Input() toPlaceholder: string = 'global.endDate';
  @Input() required: boolean = false;
  @Input() infoTooltip: string = '';
  @Input() isDisabled: boolean = false;
  @Input() showClearBtn: boolean = true;

  form: FormGroup = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  disabled = false;
  onChange: (value: DateRange) => void = () => {};
  onTouched: () => void = () => {};
  isLoaded = false;

  constructor(private translate: TranslateService) {}

  ngOnInit() {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (!this.disabled) {
        this.onChange(value as DateRange);
        this.onTouched();
      }
    });

    this.translate
      .get([this.fromPlaceholder, this.toPlaceholder])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isLoaded = true;
      });
  }

  writeValue(value: DateRange): void {
    if (value) {
      this.form.patchValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: DateRange) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  clearDatePicker(): void {
    this.form.reset();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
